import type {
  BundleContext,
  ConfigDraftResult,
  DraftableKind,
  ConfigKind,
  FlowStep,
  FlowTrace,
  JourneyRuntime,
  Page,
  RulesDiff,
  RulesOutcome,
  ValidationIssue,
} from '@cyberscore/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import type { QuestionInfo } from './conditions';
import { ownLevels, pickVariant, type ItemSummary } from './journey';
import { refreshLists } from '../../lib/list';

/** The studio's view of configuration drafts and releases (the core's /config API, decision 0004). */

export interface ConfigItem extends Omit<ItemSummary, 'title'> {
  tenant_id: string;
  body: Record<string, unknown>;
  shared: boolean;
  /** What the control library gives a platform item (task 95), for the platform's own editors. */
  library?: { body: Record<string, unknown>; updated_at: string } | null;
}

export interface Release {
  id: string;
  kind: 'publish' | 'rollback';
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  note: string;
  /** Every item in the release, as published: the diff against the live version is on the detail. */
  items: { item_id: string; revision: number }[];
  validation: ValidationIssue[];
  submitted_by: string;
  submitted_by_name: string | null;
  submitted_at: string;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string | null;
  version_number: number | null;
}

/** What the signed-in person may do in the studio, and which scopes are theirs to edit. */
export function useStudioAccess() {
  const { tenant, me } = useSession();
  return {
    sees: can(tenant, 'config.view'),
    edits: can(tenant, 'config.create', 'config.edit'),
    approves: can(tenant, 'releases.approve'),
    platform: tenant?.kind === 'platform',
    tenantId: tenant?.id ?? '',
    userId: me?.user.id ?? '',
    own: ownLevels(tenant?.kind === 'platform'),
  };
}

const k = (t: string | undefined, ...rest: unknown[]) => ['config', t, ...rest] as const;

export function useItems(kind: ConfigKind, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: k(tenant?.id, 'items', kind),
    queryFn: () => api<ItemSummary[]>('GET', `/config/items?kind=${kind}`),
    enabled: !!tenant && enabled,
  });
}

export function useItem(id: string | undefined) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: k(tenant?.id, 'item', id),
    queryFn: () => api<ConfigItem>('GET', `/config/items/${id}`),
    enabled: !!tenant && !!id,
  });
}

/** Every item of a kind with its body, e.g. all questions with their types and options. */
export function useItemBodies(kind: ConfigKind, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: k(tenant?.id, 'items', kind, 'bodies'),
    queryFn: () => api<(ItemSummary & { body: Record<string, unknown> })[]>('GET', `/config/items?kind=${kind}&include_body=true`), // prettier-ignore
    enabled: !!tenant && enabled,
    staleTime: 60_000,
  });
}

/** Several items at once, e.g. every stage of a journey. */
export function useItemsById(ids: readonly string[]) {
  const { tenant } = useSession();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: k(tenant?.id, 'item', id),
      queryFn: () => api<ConfigItem>('GET', `/config/items/${id}`),
      enabled: !!tenant,
    })),
  });
}

/** Saves a draft. Send the revision edited: someone else's newer save is a 409, never overwritten. */
export function useSaveItem() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({
      id,
      body,
      revision,
      note,
    }: {
      id: string;
      body: unknown;
      revision: number;
      note?: string;
    }) => api<ConfigItem>('PUT', `/config/items/${id}`, { body, revision, ...(note ? { note } : {}) }),
    onSuccess: (row) => qc.setQueryData(k(tenant?.id, 'item', row.id), row),
    onSettled: () => qc.invalidateQueries({ queryKey: k(tenant?.id, 'items') }),
  });
}

export function useCreateItem() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (input: {
      kind: ConfigKind;
      key: string;
      body: unknown;
      scope?: { level: string; key: string };
      note?: string;
    }) => api<ConfigItem>('POST', '/config/items', input),
    onSettled: () => qc.invalidateQueries({ queryKey: k(tenant?.id, 'items') }),
  });
}

/** The whole draft set checked by the core: references, loops, option values. */
export function useValidate() {
  return useMutation({
    mutationFn: () => api<{ ok: boolean; issues: ValidationIssue[] }>('POST', '/config/validate'),
  });
}

/** The release waiting for approval, if any; the history pages through the list view (task 60). */
export function usePendingRelease(enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: k(tenant?.id, 'releases', 'pending'),
    queryFn: () => api<Page<Release>>('GET', '/config/releases?status=pending&page_size=12').then((p) => p.items[0] ?? null), // prettier-ignore
    enabled: !!tenant && enabled,
  });
}

export interface DiffEntry {
  item_id: string;
  kind: ConfigKind;
  key: string;
  scope_level: string;
  scope_key: string;
  change: 'added' | 'changed' | 'removed';
  from_revision: number | null;
  to_revision: number | null;
}

/** One release with what it changes against the version it was based on. */
export function useRelease(id: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: k(tenant?.id, 'releases', id),
    queryFn: () => api<Release & { diff: DiffEntry[] }>('GET', `/config/releases/${id}`),
    enabled: !!tenant && !!id,
  });
}

export function useReleaseAction() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (
      a:
        | { type: 'submit'; note: string }
        | { type: 'approve'; id: string; note?: string }
        | { type: 'reject'; id: string; note: string }
        | { type: 'withdraw'; id: string },
    ) =>
      a.type === 'submit'
        ? api<Release>('POST', '/config/releases', { note: a.note })
        : api<Release>('POST', `/config/releases/${a.id}/${a.type}`, a.type === 'withdraw' ? undefined : { note: a.note }), // prettier-ignore
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: k(tenant?.id) }), refreshLists(qc)]),
  });
}

/** Every question's key, text, type and options (the author's own variant first), for condition editors. */
export function useQuestionInfo(): QuestionInfo[] {
  const bodies = useItemBodies('question');
  const { own } = useStudioAccess();
  const rows = bodies.data ?? [];
  return [...new Set(rows.map((r) => r.key))].map((key) => {
    const b = pickVariant(rows, key, own)!.body as { text?: string; type?: string; options?: { value: string; label: string }[] }; // prettier-ignore
    return { key, text: b.text ?? key, type: b.type ?? 'text', options: (b.options ?? []).map((o) => ({ value: o.value, label: o.label })) }; // prettier-ignore
  });
}

export interface DocumentTypeInfo {
  key: string;
  name: string;
  fields: { key: string; label: string; type: string }[];
}

/** Every document type with its fields (the author's own variant first), for claim checks. */
export function useDocumentTypeInfo(): DocumentTypeInfo[] {
  const bodies = useItemBodies('document_type');
  const { own } = useStudioAccess();
  const rows = bodies.data ?? [];
  return [...new Set(rows.map((r) => r.key))].sort().map((key) => {
    const b = pickVariant(rows, key, own)!.body as { name?: string; fields?: { key: string; label: string; type: string }[] }; // prettier-ignore
    return { key, name: b.name ?? key, fields: (b.fields ?? []).map((f) => ({ key: f.key, label: f.label, type: f.type })) }; // prettier-ignore
  });
}

export interface DryRunResult {
  as_of: string;
  assessment_id: string | null;
  context: BundleContext;
  published: RulesOutcome | null;
  draft: RulesOutcome;
  diff: RulesDiff;
  issues: ValidationIssue[];
}

/** Runs the draft rules (with unsaved ones) on an assessment or a made-up one. Nothing is stored. */
export function useDryRun() {
  return useMutation({
    mutationFn: (body: {
      assessment_id?: string;
      context?: Partial<BundleContext>;
      answers?: Record<string, { value: unknown } | null>;
      rules?: Record<string, unknown>;
    }) => api<DryRunResult>('POST', '/rules/dry-run', body),
  });
}

export interface TryFlowResult extends Omit<FlowTrace, 'steps'> {
  state: JourneyRuntime['state'];
  next: JourneyRuntime['next'];
  steps: (FlowStep & { text: string; type: string; options: { value: string; label: string }[] })[];
}

/**
 * A journey's path for made-up answers (task 85), over the drafts and the builder's unsaved edits.
 * Nothing is stored. Keeps the last path on screen while the next one loads.
 */
export function useTryFlow(body: {
  journey: string;
  answers: Record<string, unknown>;
  facts: Record<string, unknown>;
  edits: { journey?: unknown; stages: Record<string, unknown> };
}) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['studio', tenant?.id, 'try-flow', body],
    queryFn: () => api<TryFlowResult>('POST', '/questions/flow', body),
    enabled: !!tenant,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

/** A question or rule drafted by the agent from a plain-words request (task 47). Nothing is saved. */
export function useDraftWithAi() {
  return useMutation({
    mutationFn: (input: { kind: DraftableKind; request: string }) => api<ConfigDraftResult>('POST', '/config/drafts/ai', input),
  }); // prettier-ignore
}
